import { useState } from "react";
import {
  Alert, Box, Button, Chip, Drawer, IconButton, Link, Paper, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import {
  createNetsuiteMapComment, deleteNetsuiteMapComment, setNetsuiteMapCommentResolved,
} from "@/api/netsuiteMap";
import type { CommentKind, NsComment, NsMap } from "@/api/netsuiteMap";
import { targetLabel } from "./shared";
import type { Target } from "./shared";

// Staff comments and suggestions. With a target it shows that item's thread;
// without one it lists all feedback on the map and takes general comments.
const MAX_BODY = 2000;
export const MAP_QUERY_KEY = ["netsuite_map"];

function errorMessage(error: unknown): string {
  const data = (error as { response?: { data?: { error?: string; errors?: string[] } } })?.response?.data;
  return data?.error ?? data?.errors?.join(", ") ?? (error as Error)?.message ?? "Something went wrong";
}

function CommentForm({ target }: { target: Target }) {
  const qc = useQueryClient();
  const [kind, setKind] = useState<CommentKind>("comment");
  const [body, setBody] = useState("");
  const create = useMutation({
    mutationFn: () => createNetsuiteMapComment({ target_type: target.type, target_key: target.key, kind, body }),
    onSuccess: () => {
      setBody("");
      setKind("comment");
      qc.invalidateQueries({ queryKey: MAP_QUERY_KEY });
    },
  });
  const tooLong = body.length > MAX_BODY;

  return (
    <Stack spacing={1} component="form" onSubmit={(e) => { e.preventDefault(); create.mutate(); }}>
      <ToggleButtonGroup size="small" exclusive value={kind} onChange={(_, v: CommentKind | null) => v && setKind(v)}>
        <ToggleButton value="comment">Comment</ToggleButton>
        <ToggleButton value="suggestion">Suggestion</ToggleButton>
      </ToggleButtonGroup>
      <TextField
        multiline
        minRows={3}
        size="small"
        placeholder={kind === "suggestion" ? "What should change, and why?" : "Add a comment or a question"}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        error={tooLong}
        helperText={`${body.length}/${MAX_BODY}`}
      />
      {create.isError && <Alert severity="error">Couldn&apos;t post: {errorMessage(create.error)}</Alert>}
      <Box>
        <Button type="submit" variant="contained" disabled={!body.trim() || tooLong || create.isPending}>
          {create.isPending ? "Posting..." : "Post"}
        </Button>
      </Box>
    </Stack>
  );
}

function CommentCard({ comment, map, canResolve, showTarget, onOpenTarget }: {
  comment: NsComment;
  map: NsMap;
  canResolve: boolean;
  showTarget: boolean;
  onOpenTarget: (t: Target) => void;
}) {
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: MAP_QUERY_KEY });
  const remove = useMutation({ mutationFn: () => deleteNetsuiteMapComment(comment.id), onSuccess: refresh });
  const resolve = useMutation({ mutationFn: (v: boolean) => setNetsuiteMapCommentResolved(comment.id, v), onSuccess: refresh });
  const error = remove.error ?? resolve.error;

  return (
    <Paper variant="outlined" sx={{ p: 1.5, opacity: comment.resolved ? 0.75 : 1 }}>
      <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap">
        <Typography variant="body2" fontWeight={600}>{comment.author}</Typography>
        <Typography variant="caption" color="text.secondary">{dayjs(comment.created_at).format("MMM D, YYYY h:mm A")}</Typography>
        {comment.kind === "suggestion" && <Chip size="small" variant="outlined" color="secondary" label="Suggestion" />}
        {comment.resolved && <Chip size="small" variant="outlined" color="success" label={comment.resolved_by ? `Resolved by ${comment.resolved_by}` : "Resolved"} />}
      </Stack>
      {showTarget && (
        <Link component="button" type="button" variant="caption" onClick={() => onOpenTarget({ type: comment.target_type, key: comment.target_key })} sx={{ textAlign: "left" }}>
          On: {targetLabel(map, comment.target_type, comment.target_key)}
        </Link>
      )}
      <Typography variant="body2" sx={{ mt: 0.75, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{comment.body}</Typography>
      {(canResolve || comment.can_delete) && (
        <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
          {canResolve && (
            <Button size="small" disabled={resolve.isPending} onClick={() => resolve.mutate(!comment.resolved)}>
              {comment.resolved ? "Reopen" : "Mark resolved"}
            </Button>
          )}
          {comment.can_delete && (
            <Button size="small" color="error" disabled={remove.isPending} onClick={() => window.confirm("Delete this comment?") && remove.mutate()}>
              Delete
            </Button>
          )}
        </Stack>
      )}
      {error && <Alert severity="error" sx={{ mt: 1 }}>{errorMessage(error)}</Alert>}
    </Paper>
  );
}

type Show = "open" | "resolved" | "all";

export function CommentsDrawer({ open, target, map, comments, canResolve, onClose, onOpenTarget, onShowAll }: {
  open: boolean;
  target: Target | null;
  map: NsMap;
  comments: NsComment[];
  canResolve: boolean;
  onClose: () => void;
  onOpenTarget: (t: Target) => void;
  onShowAll: () => void;
}) {
  const [show, setShow] = useState<Show>("open");
  const thread = target
    ? comments.filter((c) => c.target_type === target.type && c.target_key === target.key)
    : comments.filter((c) => show === "all" || (show === "open" ? !c.resolved : c.resolved));
  const general: Target = { type: "general", key: "" };

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: "100%", sm: 440 } } }}>
      <Stack spacing={2} sx={{ p: 2 }}>
        <Stack direction="row" alignItems="flex-start" spacing={1}>
          {target && (
            <IconButton size="small" onClick={onShowAll} aria-label="All feedback"><ArrowBackIcon fontSize="small" /></IconButton>
          )}
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="overline" color="text.secondary">{target ? "Comments on" : "Comments and ideas"}</Typography>
            <Typography variant="h6" lineHeight={1.25}>{target ? targetLabel(map, target.type, target.key) : "All feedback on the map"}</Typography>
          </Box>
          <IconButton size="small" onClick={onClose} aria-label="Close"><CloseIcon fontSize="small" /></IconButton>
        </Stack>

        {!target && (
          <Typography variant="body2" color="text.secondary">
            Spot something wrong, missing, or worth changing? Add it here, or use the comment icon on any integration, record row or open item.
          </Typography>
        )}
        <CommentForm key={target ? `${target.type}:${target.key}` : "general"} target={target ?? general} />

        {!target && (
          <ToggleButtonGroup size="small" exclusive value={show} onChange={(_, v: Show | null) => v && setShow(v)}>
            <ToggleButton value="open">Open</ToggleButton>
            <ToggleButton value="resolved">Resolved</ToggleButton>
            <ToggleButton value="all">All</ToggleButton>
          </ToggleButtonGroup>
        )}
        {thread.length === 0 && (
          <Typography variant="body2" color="text.secondary">{target ? "No comments yet. Be the first." : "Nothing here yet."}</Typography>
        )}
        {thread.map((c) => (
          <CommentCard key={c.id} comment={c} map={map} canResolve={canResolve} showTarget={!target} onOpenTarget={onOpenTarget} />
        ))}
      </Stack>
    </Drawer>
  );
}
